export interface CommercialProposal {
  id: number;
  listingId: number;
  radioName: string;
  radioFrequency: string;
  radioAvatar: string;
  bidAmount: number;
  duration: string;
  benefits: string[];
  message: string;
  status: 'pendiente' | 'aceptada' | 'rechazada';
  submittedAt: string;
}

export interface CommercialListing {
  id: number;
  advertiser: string;
  logo: string;
  category: string;
  budget: string;
  location: string;
  title: string;
  description: string;
  status: 'abierta' | 'negociacion' | 'aceptada' | 'finalizada';
  commissionRate: number;
  radioRevenue: number;
  createdAt: string;
  closesAt: string;
  contactName: string;
  contactEmail: string;
  proposals: CommercialProposal[];
}

export const commercialCategoryLabels: Record<string, string> = {
  alimentacion: 'Alimentación',
  moda: 'Moda / Indumentaria',
  tecnologia: 'Tecnología',
  salud: 'Salud / Bienestar',
  automotriz: 'Automotriz',
  entretenimiento: 'Entretenimiento',
  construccion: 'Construcción',
  financiero: 'Servicios Financieros',
  logistica: 'Logística / Transporte',
  agroindustria: 'Agroindustria',
};

export const commercialCategoryIcons: Record<string, string> = {
  alimentacion: 'ri-restaurant-line',
  moda: 'ri-t-shirt-line',
  tecnologia: 'ri-computer-line',
  salud: 'ri-heart-pulse-line',
  automotriz: 'ri-car-line',
  entretenimiento: 'ri-movie-line',
  construccion: 'ri-building-2-line',
  financiero: 'ri-bank-line',
  logistica: 'ri-truck-line',
  agroindustria: 'ri-plant-line',
};

export const commercialStatusLabels: Record<CommercialListing['status'], string> = {
  abierta: 'Abierta',
  negociacion: 'En negociación',
  aceptada: 'Aceptada',
  finalizada: 'Finalizada',
};

export const commercialListings: CommercialListing[] = [
  {
    id: 1,
    advertiser: 'Cervecería del Valle',
    logo: 'https://readdy.ai/api/search-image?query=Craft%20beer%20brewery%20logo%20minimalist%20golden%20wheat%20hops%20icon%20warm%20amber%20gradient%20clean%20professional%20branding%20flat%20design%20modern&width=200&height=200&seq=cmcl-logo-01&orientation=squarish',
    category: 'alimentacion',
    budget: '$8,000 - $12,000/mes',
    location: 'Tucumán, Argentina',
    title: 'Campaña de Verano — Cerveza Artesanal del Valle',
    description: 'Cervecería del Valle busca posicionar su nueva línea de cervezas artesanales en el NOA. Campaña de verano que requiere presencia radial intensiva durante 4 meses en estaciones líderes de la región. El anunciante valora creatividad en la pauta y alcance comprobado. Se priorizan radios con audiencia de 25-45 años y presencia en redes sociales activa.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-10',
    closesAt: '2026-07-15',
    contactName: 'Gonzalo Pereira',
    contactEmail: 'gonzalo@cerveceriadelvalle.com',
    proposals: [
      {
        id: 101,
        listingId: 1,
        radioName: 'FM Horizonte',
        radioFrequency: '100.9 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20warm%20orange%20yellow%20gradient%20professional%20clean%20branding%20bold%20typography&width=100&height=100&seq=cmcl-radio-01&orientation=squarish',
        bidAmount: 9800,
        duration: '4 meses (Oct-Ene)',
        benefits: ['12 menciones diarias en prime time', 'Banner animado en home page', '3 eventos en vivo con degustación', 'Sorteo semanal de packs cerveceros', 'Contenido en Instagram + TikTok'],
        message: 'FM Horizonte lidera en audiencia 25-45 en Tucumán. Hacemos eventos masivos y nuestra comunidad interactúa fuerte con marcas de consumo. Esta campaña puede romperla. Ya trabajamos con marcas de bebidas y los resultados fueron excelentes.',
        status: 'pendiente',
        submittedAt: '2026-06-13',
      },
      {
        id: 102,
        listingId: 1,
        radioName: 'Radio Latitud Sur',
        radioFrequency: '97.3 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20teal%20white%20clean%20gradient%20professional%20design%20typography&width=100&height=100&seq=cmcl-radio-02&orientation=squarish',
        bidAmount: 11500,
        duration: '6 meses (Sep-Feb)',
        benefits: ['15 menciones diarias en todos los programas', 'Patrocinio exclusivo del segmento gastronómico', 'Banner premium con rotación completa', '6 eventos de degustación en puntos de venta', 'Cobertura de festivales regionales', 'Newsletter mensual a 80K suscriptores'],
        message: 'Cubrimos todo el NOA con la mejor señal. Nuestro segmento gastronómico es referencia en la región. Una campaña de 6 meses nos permite construir marca, no solo pautar. El verano en el NOA es fuerte y la cerveza artesanal está en pleno crecimiento.',
        status: 'pendiente',
        submittedAt: '2026-06-14',
      },
    ],
  },
  {
    id: 2,
    advertiser: 'Constructora del Plata',
    logo: 'https://readdy.ai/api/search-image?query=Construction%20company%20logo%20minimalist%20geometric%20modern%20architecture%20building%20icon%20charcoal%20warm%20gray%20gradient%20professional%20corporate%20branding%20clean%20design&width=200&height=200&seq=cmcl-logo-02&orientation=squarish',
    category: 'construccion',
    budget: '$15,000 - $25,000/mes',
    location: 'Córdoba, Argentina',
    title: 'Lanzamiento — Barrio Cerrado Las Acacias',
    description: 'Constructora del Plata lanza su proyecto más ambicioso: un barrio cerrado de 200 lotes en las sierras de Córdoba. Necesitan campaña radial de alto impacto para atraer compradores de CABA, Rosario y la propia Córdoba. El target es familias de alto poder adquisitivo buscando segunda vivienda o inversión. La pauta debe transmitir exclusividad, naturaleza y seguridad.',
    status: 'negociacion',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-01',
    closesAt: '2026-08-01',
    contactName: 'Martina Sosa',
    contactEmail: 'msosa@constructoraplata.com',
    proposals: [
      {
        id: 201,
        listingId: 2,
        radioName: 'Radio Capital',
        radioFrequency: '104.3 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20gold%20black%20gradient%20luxury%20style%20professional%20clean%20typography%20bold&width=100&height=100&seq=cmcl-radio-03&orientation=squarish',
        bidAmount: 21000,
        duration: '6 meses (Ago-Ene)',
        benefits: ['20 menciones diarias en programas premium', 'Patrocinio de noticiero matutino', 'Banner home page full width', '12 entrevistas con el director del proyecto', '2 eventos exclusivos para oyentes en el predio', 'Remarketing digital integrado'],
        message: 'Radio Capital es líder en CABA y tiene llegada directa al perfil de comprador de barrios cerrados. Nuestra audiencia de 35-60 años con poder adquisitivo alto es exactamente el target. Podemos armar una campaña integral radio + digital que mida conversiones reales.',
        status: 'pendiente',
        submittedAt: '2026-06-05',
      },
      {
        id: 202,
        listingId: 2,
        radioName: 'FM Milenium',
        radioFrequency: '91.7 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20cool%20gray%20silver%20gradient%20clean%20sophisticated%20professional%20typography&width=100&height=100&seq=cmcl-radio-04&orientation=squarish',
        bidAmount: 18500,
        duration: '5 meses (Ago-Dic)',
        benefits: ['15 menciones diarias', 'Segmento inmobiliario semanal de 10 min', 'Banner lateral premium', '4 visitas guiadas al predio para oyentes', 'Campaña en newsletter de Real Estate'],
        message: 'Tenemos el programa de Real Estate más escuchado de Córdoba. Nuestra audiencia está compuesta por inversores activos que ya compran propiedades. La preventa de Las Acacias puede ser un éxito con una campaña bien segmentada en nuestra emisora.',
        status: 'pendiente',
        submittedAt: '2026-06-04',
      },
      {
        id: 203,
        listingId: 2,
        radioName: 'Estación 98',
        radioFrequency: '98.1 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20navy%20cream%20elegant%20gradient%20professional%20clean%20sophisticated%20typography&width=100&height=100&seq=cmcl-radio-05&orientation=squarish',
        bidAmount: 23000,
        duration: '8 meses (Jul-Feb)',
        benefits: ['25 menciones diarias con rotación inteligente', 'Sponsor principal del programa de la tarde', 'Banner premium con video', '8 eventos VIP en el predio', 'Campaña de influencers locales', 'Dashboard de métricas en tiempo real', 'Podcast exclusivo sobre el proyecto'],
        message: 'Somos la radio aspiracional de Córdoba. Nuestra audiencia valora el lifestyle premium que Las Acacias representa. Una campaña de 8 meses nos permite acompañar todo el ciclo de venta: desde intriga hasta cierre. Incluimos medición de leads y atribución.',
        status: 'pendiente',
        submittedAt: '2026-06-06',
      },
    ],
  },
  {
    id: 3,
    advertiser: 'TechFlow',
    logo: 'https://readdy.ai/api/search-image?query=Technology%20company%20logo%20minimalist%20geometric%20digital%20flow%20lines%20coral%20emerald%20gradient%20modern%20startup%20clean%20professional%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-03&orientation=squarish',
    category: 'tecnologia',
    budget: '$6,000 - $9,000/mes',
    location: 'Rosario, Argentina',
    title: 'App de Delivery de Cercanía — TechFlow Express',
    description: 'TechFlow lanza su app de delivery hiperlocal en Rosario. La campaña radial busca posicionar la marca entre comercios y consumidores finales. Necesitan alcance masivo pero con segmentación geográfica precisa. La propuesta debe incluir métricas de descarga de app y registro de comercios.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-15',
    closesAt: '2026-07-20',
    contactName: 'Luciano Ferrer',
    contactEmail: 'luciano@techflow.ar',
    proposals: [],
  },
  {
    id: 4,
    advertiser: 'Moda Urbana',
    logo: 'https://readdy.ai/api/search-image?query=Fashion%20brand%20logo%20minimalist%20modern%20urban%20style%20coral%20pink%20warm%20gradient%20clean%20professional%20clothing%20retail%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-04&orientation=squarish',
    category: 'moda',
    budget: '$4,000 - $7,000/mes',
    location: 'Buenos Aires, Argentina',
    title: 'Colección Primavera-Verano 2027',
    description: 'Moda Urbana presenta su colección más grande del año. Buscan pauta radial en CABA y GBA para campaña de lanzamiento con foco en moda joven y accesible. La campaña debe conectar con audiencia de 18-35 años, con fuerte presencia digital complementaria. Se valora creatividad en formatos no tradicionales.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-16',
    closesAt: '2026-08-01',
    contactName: 'Camila Ortega',
    contactEmail: 'camila@modaurbana.com.ar',
    proposals: [
      {
        id: 401,
        listingId: 4,
        radioName: 'Pop Radio 101',
        radioFrequency: '101.5 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Pop%20radio%20station%20logo%20circle%20modern%20vibrant%20coral%20pink%20gradient%20youthful%20energetic%20style%20clean%20typography%20professional&width=100&height=100&seq=cmcl-radio-06&orientation=squarish',
        bidAmount: 5200,
        duration: '3 meses (Sep-Nov)',
        benefits: ['18 menciones diarias en rotación', 'Concurso de outfit del día en vivo', 'Banner interactivo en home', '2 desfiles en vivo desde el estudio', 'Influencers de la radio usando la marca'],
        message: 'Somos LA radio pop de Buenos Aires. Nuestra audiencia 18-30 es exactamente quien compra Moda Urbana. El concurso de outfit genera engagement masivo y las fotos en redes se vuelven virales. Hagamos algo disruptivo juntos.',
        status: 'pendiente',
        submittedAt: '2026-06-16',
      },
    ],
  },
  {
    id: 5,
    advertiser: 'AgroNova',
    logo: 'https://readdy.ai/api/search-image?query=Agribusiness%20company%20logo%20minimalist%20modern%20wheat%20grain%20leaf%20icon%20earthy%20green%20gold%20gradient%20clean%20professional%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-05&orientation=squarish',
    category: 'agroindustria',
    budget: '$10,000 - $18,000/mes',
    location: 'Rosario y zona núcleo, Argentina',
    title: 'Fertilizantes Inteligentes AgroNova — Campaña de Siembra',
    description: 'AgroNova líder en fertilizantes de precisión busca campaña radial para la temporada de siembra gruesa 2026-2027. El target son productores agropecuarios de la zona núcleo. La pauta debe estar presente en radios rurales y programas especializados del agro. Se valora conocimiento del sector y llegada comprobada al productor.',
    status: 'negociacion',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-05-28',
    closesAt: '2026-07-28',
    contactName: 'Esteban Macías',
    contactEmail: 'emacias@agronova.com',
    proposals: [
      {
        id: 501,
        listingId: 5,
        radioName: 'LT8 Radio Rosario',
        radioFrequency: 'AM 830',
        radioAvatar: 'https://readdy.ai/api/search-image?query=AM%20radio%20station%20logo%20classic%20traditional%20gold%20navy%20professional%20clean%20typography%20heritage%20style&width=100&height=100&seq=cmcl-radio-07&orientation=squarish',
        bidAmount: 12000,
        duration: '5 meses (Sep-Ene)',
        benefits: ['Segmento agropecuario diario de 15 min', '12 menciones diarias en programa rural', 'Banner en portal agro de la radio', 'Cobertura exclusiva de ExpoAgro', 'Entrevistas con ingenieros de AgroNova', 'Boletín semanal a productores'],
        message: 'Somos la radio con mayor llegada al productor agropecuario de la zona núcleo. Nuestro programa rural tiene 45 años al aire. El productor confía en nosotros para tomar decisiones de compra. Esta campaña tiene ROI asegurado.',
        status: 'aceptada',
        submittedAt: '2026-05-30',
      },
      {
        id: 502,
        listingId: 5,
        radioName: 'Radio Campo',
        radioFrequency: '96.3 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Rural%20radio%20station%20logo%20modern%20minimalist%20earthy%20green%20brown%20gradient%20agricultural%20style%20clean%20professional%20typography&width=100&height=100&seq=cmcl-radio-08&orientation=squarish',
        bidAmount: 14000,
        duration: '6 meses (Ago-Ene)',
        benefits: ['Programa agro exclusivo con AgroNova', '20 menciones diarias', 'Banner premium en web y app rural', 'Cobertura de 10 ferias agropecuarias', 'Microprograma técnico diario', 'WhatsApp broadcast a 15K productores'],
        message: 'Radio Campo es 100% agro. No hablamos de otra cosa. Nuestra audiencia son exclusivamente productores y contratistas. AgroNova va a estar en el oído del campo argentino durante toda la siembra gruesa.',
        status: 'pendiente',
        submittedAt: '2026-06-01',
      },
    ],
  },
  {
    id: 6,
    advertiser: 'Banco Regional del Litoral',
    logo: 'https://readdy.ai/api/search-image?query=Bank%20logo%20minimalist%20modern%20geometric%20shield%20icon%20warm%20emerald%20gold%20gradient%20clean%20corporate%20professional%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-06&orientation=squarish',
    category: 'financiero',
    budget: '$5,000 - $8,000/mes',
    location: 'Santa Fe, Argentina',
    title: 'Créditos Hipotecarios — Tu Primera Casa',
    description: 'El Banco Regional del Litoral lanza su nueva línea de créditos hipotecarios con la tasa más baja del mercado. La campaña radial busca llegar a familias jóvenes de Santa Fe, Paraná y Rosario. Necesitan generar confianza y explicar los beneficios de forma clara y cercana en un contexto económico donde la vivienda propia es un sueño alcanzable.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-14',
    closesAt: '2026-08-14',
    contactName: 'Andrea Roldán',
    contactEmail: 'aroldan@bancoregional.com',
    proposals: [
      {
        id: 601,
        listingId: 6,
        radioName: 'FM Sol',
        radioFrequency: '94.5 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circle%20modern%20minimalist%20warm%20golden%20yellow%20gradient%20friendly%20approachable%20clean%20professional%20typography&width=100&height=100&seq=cmcl-radio-09&orientation=squarish',
        bidAmount: 6200,
        duration: '4 meses (Ago-Nov)',
        benefits: ['10 menciones diarias en matutino', 'Microsegmento "Tu Primera Casa" de 3 min', 'Banner en sección economía', '4 charlas en vivo con asesores del banco', 'Sorteo de asesoramiento gratuito'],
        message: 'FM Sol es la radio de la familia santafesina. La gente confía en nosotros para tomar decisiones importantes. Un crédito hipotecario se vende con cercanía y claridad, justo lo que sabemos hacer. El microsegmento diario educa y convierte.',
        status: 'pendiente',
        submittedAt: '2026-06-15',
      },
    ],
  },
  {
    id: 7,
    advertiser: 'LogiTrans',
    logo: 'https://readdy.ai/api/search-image?query=Logistics%20company%20logo%20minimalist%20modern%20arrow%20movement%20icon%20charcoal%20orange%20gradient%20clean%20professional%20corporate%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-07&orientation=squarish',
    category: 'logistica',
    budget: '$3,500 - $6,000/mes',
    location: 'Mendoza, Argentina',
    title: 'Servicio de Logística Integral para Pymes',
    description: 'LogiTrans amplía su operación a Mendoza y busca posicionarse como el socio logístico de las pymes de la región. Campaña radial enfocada en dueños de pequeñas y medianas empresas que necesitan externalizar su logística. El mensaje: ahorro de costos, eficiencia y cobertura regional completa.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-12',
    closesAt: '2026-07-25',
    contactName: 'Federico Lagos',
    contactEmail: 'flagos@logitrans.com.ar',
    proposals: [],
  },
  {
    id: 8,
    advertiser: 'SaludVital',
    logo: 'https://readdy.ai/api/search-image?query=Healthcare%20company%20logo%20minimalist%20modern%20heart%20pulse%20icon%20fresh%20mint%20green%20warm%20gradient%20clean%20professional%20medical%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-08&orientation=squarish',
    category: 'salud',
    budget: '$7,000 - $10,000/mes',
    location: 'Mar del Plata, Argentina',
    title: 'Plan de Salud Familiar — Preventiva Total',
    description: 'SaludVital lanza su plan de salud familiar más completo con foco en medicina preventiva. La campaña radial debe llegar a familias de Mar del Plata y la costa atlántica. El diferencial es la cobertura odontológica y oftalmológica incluida sin coseguro. Buscan pauta en radios con llegada a padres y madres de 30-55 años.',
    status: 'aceptada',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-05-15',
    closesAt: '2026-06-15',
    contactName: 'Verónica Dávila',
    contactEmail: 'vdavila@saludvital.com',
    proposals: [
      {
        id: 801,
        listingId: 8,
        radioName: 'Radio Atlántica',
        radioFrequency: '102.7 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Coastal%20radio%20station%20logo%20circle%20modern%20minimalist%20sea%20blue%20white%20fresh%20gradient%20clean%20professional%20typography&width=100&height=100&seq=cmcl-radio-10&orientation=squarish',
        bidAmount: 8500,
        duration: '6 meses (Jul-Dic)',
        benefits: ['14 menciones diarias en programas familiares', 'Segmento semanal "Salud en Familia" de 10 min', 'Banner premium en home', '6 charlas abiertas con médicos de SaludVital', 'Campaña integrada con redes sociales de la radio', 'Encuesta de salud para oyentes con resultados'],
        message: 'Somos la radio más escuchada de la costa. Nuestro programa matutino familiar tiene 60% de audiencia femenina, justo quien decide el plan de salud del hogar. El segmento semanal genera engagement real y las charlas con médicos construyen confianza. SaludVital va a ser sinónimo de prevención en Mar del Plata.',
        status: 'aceptada',
        submittedAt: '2026-05-20',
      },
    ],
  },
  {
    id: 9,
    advertiser: 'FiestaBox',
    logo: 'https://readdy.ai/api/search-image?query=Event%20entertainment%20company%20logo%20minimalist%20modern%20party%20confetti%20balloon%20icon%20vibrant%20coral%20yellow%20warm%20gradient%20clean%20playful%20professional%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-09&orientation=squarish',
    category: 'entretenimiento',
    budget: '$2,500 - $4,500/mes',
    location: 'CABA, Argentina',
    title: 'FiestaBox — La App de Fiestas y Eventos',
    description: 'FiestaBox es la nueva app que conecta organizadores de eventos con proveedores de servicios: catering, DJ, fotografía, decoración y más. La campaña radial busca darla a conocer en CABA previo a la temporada de eventos de fin de año. Target: jóvenes profesionales 25-40 que organizan eventos corporativos y sociales.',
    status: 'abierta',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-06-17',
    closesAt: '2026-08-17',
    contactName: 'Tomás Guzmán',
    contactEmail: 'tomas@fiestabox.ar',
    proposals: [],
  },
  {
    id: 10,
    advertiser: 'AutoParts Pro',
    logo: 'https://readdy.ai/api/search-image?query=Automotive%20parts%20company%20logo%20minimalist%20modern%20gear%20wrench%20icon%20dark%20charcoal%20orange%20red%20gradient%20clean%20professional%20industrial%20branding%20flat%20design&width=200&height=200&seq=cmcl-logo-10&orientation=squarish',
    category: 'automotriz',
    budget: '$4,000 - $7,000/mes',
    location: 'Córdoba y zona centro, Argentina',
    title: 'Repuestos Originales — Garantía Total',
    description: 'AutoParts Pro es el nuevo distribuidor oficial de repuestos originales para las principales marcas en el centro del país. Campaña radial para posicionar la marca entre talleres mecánicos y dueños de vehículos. Mensaje: repuestos originales al mejor precio con garantía total de fábrica. Cobertura en Córdoba, Villa María, Río Cuarto y San Francisco.',
    status: 'finalizada',
    commissionRate: 0,
    radioRevenue: 100,
    createdAt: '2026-03-01',
    closesAt: '2026-05-01',
    contactName: 'Pablo Márquez',
    contactEmail: 'pmarquez@autopartspro.com',
    proposals: [
      {
        id: 1001,
        listingId: 10,
        radioName: 'Cadena Centro',
        radioFrequency: '99.9 FM',
        radioAvatar: 'https://readdy.ai/api/search-image?query=Radio%20station%20network%20logo%20modern%20minimalist%20corporate%20orange%20charcoal%20gradient%20clean%20professional%20typography%20regional%20style&width=100&height=100&seq=cmcl-radio-11&orientation=squarish',
        bidAmount: 5500,
        duration: '3 meses (Mar-May)',
        benefits: ['16 menciones diarias en cadena regional', 'Segmento automotor semanal de 8 min', 'Banner en portal de noticias', '4 eventos en talleres seleccionados', 'Sorteo de vouchers de service'],
        message: 'Cadena Centro cubre toda la región con una sola contratación. Llegamos a 12 ciudades del centro con la misma señal. Los talleres mecánicos nos escuchan todo el día. AutoParts Pro se posicionó rapidísimo como referencia de repuestos originales.',
        status: 'aceptada',
        submittedAt: '2026-03-05',
      },
    ],
  },
];